import React, { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Typography, Grid, Chip, IconButton,
  Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Button,
} from "@mui/material";
import {
  Refresh, Delete, CheckCircle, Error, Warning,
  Memory, Storage, Speed, Timer,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const STATUS_COLORS = {
  online: { bg: "rgba(37,211,102,0.1)", color: "#25D366", label: "Online" },
  degraded: { bg: "rgba(245,158,11,0.1)", color: "#F59E0B", label: "Degraded" },
  offline: { bg: "rgba(239,68,68,0.1)", color: "#EF4444", label: "Offline" },
  pending: { bg: "rgba(107,72,255,0.1)", color: "#6B48FF", label: "Pending" },
  suspended: { bg: "rgba(239,68,68,0.1)", color: "#EF4444", label: "Suspended" },
};

export default function HubsPage({ showSnackbar }) {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);

  const loadHubs = async () => {
    try {
      setLoading(true);
      const { data } = await hubApi.getHubList();
      setHubs(data.hubs || []);
    } catch (err) {
      console.error("Hubs error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHubs(); }, []);

  const handleDelete = async (hubId) => {
    try {
      await hubApi.deleteHub(hubId);
      showSnackbar("Hub removido");
      loadHubs();
    } catch (err) {
      showSnackbar(err.response?.data?.error || "Erro ao remover", "error");
    }
    setDeleteDialog(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#6B48FF" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>HUBs Locais</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            {hubs.length} hub(s) registrado(s)
          </Typography>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton onClick={loadHubs} sx={{ color: "#6B7280" }}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      {hubs.length === 0 ? (
        <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
          <CardContent sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#6B7280" }}>Nenhum HUB LOCAL registrado</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {hubs.map((hub) => {
            const statusConfig = STATUS_COLORS[hub.status] || STATUS_COLORS.pending;
            const telemetry = hub.lastTelemetry || {};
            const heartbeatAge = hub.lastHeartbeat
              ? Math.round((Date.now() - new Date(hub.lastHeartbeat).getTime()) / 1000)
              : null;

            return (
              <Grid item xs={12} md={6} key={hub.id}>
                <Card sx={{
                  bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 3, transition: "border-color 0.2s",
                  "&:hover": { borderColor: "rgba(107,72,255,0.3)" },
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: "#fff", fontSize: 15 }}>
                          {hub.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
                          ID: {hub.id} — v{hub.version} ({hub.updateChannel})
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          size="small"
                          label={statusConfig.label}
                          sx={{ bgcolor: statusConfig.bg, color: statusConfig.color, fontSize: 11, height: 22 }}
                        />
                        <Tooltip title="Remover hub">
                          <IconButton size="small" onClick={() => setDeleteDialog(hub)} sx={{ color: "#6B7280" }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Metrics */}
                    <Grid container spacing={1.5}>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Timer sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>Heartbeat</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: heartbeatAge && heartbeatAge < 120 ? "#25D366" : "#F59E0B" }}>
                            {heartbeatAge ? `${heartbeatAge}s` : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Memory sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>RAM</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#D1D5DB" }}>
                            {telemetry.memoryUsageMB ? `${telemetry.memoryUsageMB}MB` : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Speed sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>CPU</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#D1D5DB" }}>
                            {telemetry.cpuLoad != null ? `${(telemetry.cpuLoad * 100).toFixed(0)}%` : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Storage sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>Queue</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: telemetry.queueSize > 0 ? "#F59E0B" : "#D1D5DB" }}>
                            {telemetry.queueSize ?? "—"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Clone warning */}
                    {hub.cloneSuspicionScore > 30 && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: "rgba(245,158,11,0.06)", borderRadius: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Warning sx={{ fontSize: 14, color: "#F59E0B" }} />
                        <Typography sx={{ fontSize: 11, color: "#F59E0B" }}>
                          Clone suspicion score: {hub.cloneSuspicionScore}
                        </Typography>
                      </Box>
                    )}

                    {/* Footer */}
                    <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 10, color: "#4B5563" }}>
                        Host: {hub.hostname || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: "#4B5563" }}>
                        Registrado: {new Date(hub.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: { bgcolor: "#111827", borderRadius: 3 } }}>
        <DialogTitle sx={{ color: "#fff" }}>Remover HUB?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#9CA3AF" }}>
            O hub "{deleteDialog?.name}" sera desativado e seus tokens invalidados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} sx={{ color: "#6B7280" }}>Cancelar</Button>
          <Button onClick={() => handleDelete(deleteDialog?.id)} variant="contained" color="error">Remover</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
